import { ApolloError } from '@apollo/client';
import { useDisclosure } from '@chakra-ui/react';
import * as React from 'react';

import {
  NotificationFragment,
  NotificationsFilterNameEnum,
  useGetAllNotificationsQuery,
  useGetImportantNotificationsQuery,
  useGetNotificationsCountQuery,
} from '../generated/notifications';
import { useActiveApiLanguage } from '../hooks/useActiveApiLanguage';
import { createContext } from '../hooks/useContext';
import Logger from '../utils/Logger';
import { useAuth } from './AuthProvider';

export type NewsCenterNotification = {
  cursor: string;
  node: NotificationFragment;
};

export type NewsCenterNotificationNode = {
  [node: string]: NotificationFragment;
};

export type NewsCenterContext = {
  selectedNotification: NotificationFragment | null;
  setSelectedNotification: React.Dispatch<
    React.SetStateAction<NotificationFragment | null>
  >;
  hasUnreadNotifications: boolean;
  hasImportantUnreadNotifications: boolean;
  allCountDataRefetch: any;
  importantCountDataRefetch: any;
  importantNotificationEdges: NewsCenterNotification[];
  importantLoading: boolean;
  importantFetchMore: any;
  importantHasNextPage: boolean;
  importantError: ApolloError | undefined;
  allNotificationEdges: NewsCenterNotification[];
  allLoading: boolean;
  allFetchMore: any;
  allHasNextPage: boolean;
  allError: ApolloError | undefined;
  listTabIndex: number;
  setListTabIndex: React.Dispatch<React.SetStateAction<number>>;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onToggle: () => void;
};

export const [, useNewsCenter, newsCenterContext] =
  createContext<NewsCenterContext>({
    name: 'NewsCenterContext',
    strict: true,
    errorMessage:
      'useNewsCenter: `context` is undefined. Seems you forgot to wrap component within the Provider',
  });
export const NewsCenterProvider: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const { isOpen, onOpen, onClose, onToggle } = useDisclosure();
  const lang = useActiveApiLanguage();
  const { isAuthenticated } = useAuth();
  const [isInitialDelayDone, setIsInitialDelayDone] = React.useState(false);

  // To mitigate showing notifications of previously logged in users:
  // initialy prevent queries to run until a delay is through
  // todo: Investigate route cause of cancelled requests and uncleared caches
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsInitialDelayDone(true);
    }, 250);
    return () => clearTimeout(timeoutId);
  }, [setIsInitialDelayDone]);

  const skip = !isAuthenticated || !isInitialDelayDone;

  const [selectedNotification, setSelectedNotification] =
    React.useState<NotificationFragment | null>(null);
  const [listTabIndex, setListTabIndex] = React.useState<number>(0);
  const {
    data: dataImportant,
    loading: importantLoading,
    fetchMore: importantFetchMore,
    error: importantError,
  } = useGetImportantNotificationsQuery({
    variables: { lang: lang, count: 8, cursor: '' },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: false,
    skip,
    onError: (error) => {
      Logger.error(error);
    },
  });
  const { importantNotificationEdges, importantHasNextPage } =
    React.useMemo(() => {
      return {
        importantNotificationEdges: dataImportant?.notifications?.pagination
          ?.edges as NewsCenterNotification[],
        importantHasNextPage:
          dataImportant?.notifications?.pagination?.pageInfo.hasNextPage ??
          false,
      };
    }, [dataImportant]);

  const {
    data: dataAll,
    loading: allLoading,
    fetchMore: allFetchMore,
    error: allError,
  } = useGetAllNotificationsQuery({
    variables: { lang: lang, count: 8, cursor: '' },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: false,
    skip,
    onError: (error) => {
      Logger.error(error);
    },
  });
  const { allNotificationEdges, allHasNextPage } = React.useMemo(() => {
    return {
      allNotificationEdges: dataAll?.notifications?.pagination
        ?.edges as NewsCenterNotification[],
      allHasNextPage:
        dataAll?.notifications?.pagination?.pageInfo.hasNextPage ?? false,
    };
  }, [dataAll]);

  const { data: importantCountData, refetch: importantCountDataRefetch } =
    useGetNotificationsCountQuery({
      variables: { name: NotificationsFilterNameEnum.ImportantUnread },
      skip,
      onError: (error) => {
        Logger.error(error);
      },
    });
  const hasImportantUnreadNotifications = React.useMemo(() => {
    return (importantCountData?.notifications?.pagination?.totalCount ?? 0) > 0;
  }, [importantCountData]);

  const { data: allCountData, refetch: allCountDataRefetch } =
    useGetNotificationsCountQuery({
      variables: { name: NotificationsFilterNameEnum.AllUnread },
      skip,
      onError: (error) => {
        Logger.error(error);
      },
    });
  const hasUnreadNotifications = React.useMemo(() => {
    return (allCountData?.notifications?.pagination?.totalCount ?? 0) > 0;
  }, [allCountData]);

  const context = React.useMemo<NewsCenterContext>(
    () => ({
      isOpen,
      selectedNotification,
      setSelectedNotification,
      listTabIndex,
      setListTabIndex,
      importantNotificationEdges,
      importantLoading,
      importantFetchMore,
      importantHasNextPage,
      importantError,
      allNotificationEdges,
      allFetchMore,
      allLoading,
      allHasNextPage,
      allError,
      hasUnreadNotifications,
      allCountDataRefetch,
      hasImportantUnreadNotifications,
      importantCountDataRefetch,
      onOpen,
      onClose,
      onToggle,
    }),
    [
      isOpen,
      selectedNotification,
      setSelectedNotification,
      listTabIndex,
      setListTabIndex,
      importantNotificationEdges,
      importantLoading,
      importantFetchMore,
      importantHasNextPage,
      importantError,
      allNotificationEdges,
      allLoading,
      allFetchMore,
      allHasNextPage,
      allError,
      hasUnreadNotifications,
      hasImportantUnreadNotifications,
      allCountDataRefetch,
      importantCountDataRefetch,
      onOpen,
      onClose,
      onToggle,
    ]
  );

  return <newsCenterContext.Provider value={context} children={children} />;
};
